import "./UsageTiers.scss";

import { useMemo } from "react";

import { usageTierTypes } from "../../../../constants/usage";
import type { PlaygroundModel } from "../../../../hooks/reactQueryHooks/usePlayground";
import type {
  ITokenUsageTiers,
  IUsageInfo,
} from "../../../../hooks/reactQueryHooks/useUsage";
import {
  formatCurrency,
  formatCurrencyNoCent,
  formatPercentage,
} from "../../../../utils/NumbersFormatting";
import type { UsageTierItemProps } from "./UsageTierItem";
import { UsageTierItem } from "./UsageTierItem";

interface UsageTiersProps {
  usageInfo: IUsageInfo;
  tokenUsageTiers: ITokenUsageTiers;
  currentTierId: number;
  isStripe: boolean;
  models: PlaygroundModel[] | undefined;
}

const UsageTiers: React.FC<UsageTiersProps> = ({
  usageInfo,
  isStripe,
  currentTierId,
  tokenUsageTiers,
  models,
}) => {
  const { cogCachePrice } = usageInfo;

  const tiersData = useMemo(() => {
    if (!models) return [];

    return tokenUsageTiers.tiers.reduce<UsageTierItemProps[]>((acc, tier) => {
      if (tier.id === currentTierId || tier.id === currentTierId + 1) {
        const isLast = currentTierId === tokenUsageTiers.tiers.length - 1;
        acc.push({
          type: tier.id,
          icon: usageTierTypes[tier.id].icon,
          isCurrent: tier.id === currentTierId,
          isLast,
          title: `Tier ${tier.id} - ${usageTierTypes[tier.id].type}`,
          range: `${formatCurrencyNoCent(tier.from)} - ${formatCurrencyNoCent(
            tier.to,
          )}`,
          discounts: tier.discounts.map(
            discount =>
              `${formatPercentage(discount.discount)} discount for ${
                models?.find(({ model }) => model === discount.key)
                  ?.displayName || discount.key
              }`,
          ),
          upgrade: isLast
            ? "Congratulations! You’re on the last tier and getting the best discounts."
            : `${
                isStripe ? "Purchase credits" : "Consume tokens"
              } worth of ${formatCurrency(
                tier.to - cogCachePrice,
              )} to upgrade to the next tier.`,
        });
      }
      return acc;
    }, []);
  }, [currentTierId, tokenUsageTiers.tiers, cogCachePrice, isStripe, models]);

  return (
    <>
      <h4 className="billing-table-title mb-2">Tier</h4>
      <p className="billing__caption">
        Your organization is currently in&nbsp;
        <b>{tiersData.find(tier => tier.type === currentTierId)?.title}.</b>
      </p>
      <p className="billing__caption">
        {isStripe
          ? "The discounts will automatically increase as you upgrade tiers based on the current monthly spend."
          : "The discounts will automatically increase once you move to the next tier based on the current monthly cost."}
      </p>

      {tiersData.map(tier => (
        <UsageTierItem key={tier.type} {...tier} />
      ))}
    </>
  );
};

export { UsageTiers };
